import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useSiteContent } from '../context/SiteContentContext';
import { useLanguage } from '../context/LanguageContext';
import { SUBTITLE_LABELS } from '../utils/subtitles';
import { Users, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to extract YouTube video ID from various URL formats
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// YouTube Embed Component
const YouTubeEmbed = ({ videoId }) => (
  <div className="relative bg-slate-900 aspect-video">
    <iframe
      className="absolute inset-0 w-full h-full"
      src={`https://www.youtube.com/embed/${videoId}?rel=0`}
      title="YouTube video"
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  </div>
);

const TeamMemberCard = ({ member, language }) => {
  const videoRef = useRef(null);
  const videoUrl = member?.video || '';
  const youtubeId = getYouTubeVideoId(videoUrl);

  // Get subtitle URL based on current language
  const getSubtitleUrl = () => {
    if (language === 'en') return ''; // No subtitles for English by design
    const subtitles = member?.subtitles || {};
    return subtitles[language] || '';
  };

  // Update subtitle track when language changes (only for non-YouTube videos)
  useEffect(() => {
    // Skip if YouTube video or no video ref
    if (youtubeId || !videoRef.current || !videoUrl) return;
    
    const video = videoRef.current;
    
    // Safely remove existing tracks that are actual children of the video element
    try {
      const existingTracks = Array.from(video.getElementsByTagName('track'));
      existingTracks.forEach(track => {
        if (track.parentNode === video) {
          video.removeChild(track);
        }
      });
    } catch (e) {
      // Ignore removal errors
    }
    
    // Add new track if subtitle URL exists
    const subtitleUrl = getSubtitleUrl();
    if (subtitleUrl) {
      const track = document.createElement('track');
      track.kind = 'subtitles';
      
      track.label = SUBTITLE_LABELS[language] || language.toUpperCase();
      
      track.srclang = language;
      track.src = subtitleUrl.startsWith('/') ? `${BACKEND_URL}${subtitleUrl}` : subtitleUrl;
      track.default = true;
      video.appendChild(track);
    }
  }, [language, videoUrl, youtubeId]);

  // Get localized bio
  const getBio = () => {
    const bio = member?.bio;
    if (!bio) return '';
    if (typeof bio === 'string') return bio;
    return bio[language] || bio.en || '';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group"
    >
      {/* Video Section */}
      {videoUrl && (
        youtubeId ? (
          <YouTubeEmbed videoId={youtubeId} />
        ) : (
          <div className="relative bg-slate-900 aspect-video">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              controls
              playsInline
              preload="metadata"
              crossOrigin="anonymous"
            >
              <source 
                src={videoUrl.startsWith('/') ? `${BACKEND_URL}${videoUrl}` : videoUrl} 
                type="video/mp4" 
              />
              {getSubtitleUrl() && (
                <track
                  kind="subtitles"
                  label={language === 'ko' ? 'Korean' : language === 'ja' ? 'Japanese' : 'English'}
                  srcLang={language}
                  src={getSubtitleUrl().startsWith('/') ? `${BACKEND_URL}${getSubtitleUrl()}` : getSubtitleUrl()}
                  default
                />
              )}
            </video>
          </div>
        )
      )}

      {/* Content Section */}
      <div className="p-6">
        <h3 className="text-xl font-semibold text-slate-900 mb-1">
          {member.name_i18n?.[language] || member.name}
        </h3>
        <p className="text-slate-600 font-medium mb-4">
          {member.role_i18n?.[language] || member.role}
        </p>
        {getBio() && (
          <p className="text-slate-600 text-sm leading-relaxed">
            {getBio()}
          </p>
        )}
      </div>
    </motion.div>
  );
};

const Team = () => {
  const { siteContent } = useSiteContent();
  const { language } = useLanguage();
  const teamPage = siteContent?.teamPage || {};

  // State for team members from new API
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch team members from the new dedicated endpoint
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/team-members`);
        setTeamMembers(response.data.teamMembers || []);
      } catch (error) {
        // Team fetch failed - show empty state
        setTeamMembers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTeamMembers();
  }, []);

  // Separate team members by category and sort by order
  const managementTeam = teamMembers
    .filter(member => member.category === 'Management Team' || member.category === 'management')
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const teachersTeam = teamMembers
    .filter(member => member.category === "Teachers' Team" || member.category === 'teachers')
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const hasTeamMembers = teamMembers.length > 0;

  // Respect the Admin → CMS → Visibility "Team Page" toggle. When set to
  // false the whole page (and any deep-links to it) redirects to Home so the
  // section is fully off — this check runs after all hooks to stay compliant
  // with the rules of hooks.
  if (teamPage.visible === false) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <section className="bg-white border-b border-slate-100 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-6">
              <Users className="w-8 h-8 text-slate-700" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-slate-900 mb-4">
              {teamPage.title || 'Meet Our Team'}
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {teamPage.subtitle || 'Dedicated professionals passionate about your language learning journey'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900" />
            </div>
          </div>
        </section>
      )}

      {/* Teachers Team Section */}
      {!loading && teachersTeam.length > 0 && (
        <section className="py-16 sm:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-3 mb-10"
            >
              <Users className="w-6 h-6 text-slate-700" />
              <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">
                {teamPage.teachersTitle || "Our Teachers' Team"}
              </h2>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teachersTeam.map((member) => (
                <TeamMemberCard 
                  key={member.id} 
                  member={member}
                  language={language} 
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Management Team Section */}
      {!loading && managementTeam.length > 0 && (
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-3 mb-10"
            >
              <Award className="w-6 h-6 text-slate-700" />
              <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">
                {teamPage.managementTitle || 'Management Team'}
              </h2>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {managementTeam.map((member) => (
                <TeamMemberCard 
                  key={member.id} 
                  member={member}
                  language={language} 
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Empty State */}
      {!loading && !hasTeamMembers && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-6" />
            <h2 className="text-2xl font-semibold text-slate-700 mb-2">
              {teamPage.emptyTitle || 'Team Coming Soon'}
            </h2>
            <p className="text-slate-500">
              {teamPage.emptySubtitle || 'We\'re building our team. Check back soon to meet our talented professionals!'}
            </p>
          </div>
        </section>
      )}
    </div>
  );
};

export default Team;
